import { Alert, Dialog, DialogContent, DialogTitle, IconButton, Snackbar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AiChatDialogContent from "./dialogs/AiChatDialogContent";
import CaptureGridDialog from "./CaptureGridDialog";

export default function ShowTileDialogStack({
  isMobile,
  // AI dialog
  aiOpen,
  onAiClose,
  slideSubject = "histology",
  aiError,
  captureLoading,
  capturedImage,
  aiMessages,
  aiQuestion,
  setAiQuestion,
  onSend,
  aiLoading,
  captureLoadingForDisable,
  chatMessagesRef,
  hasDrawing,
  onClearDrawing,
  getContextTitle,
  setupDrawingCanvas,
  baseImageRef,
  drawCanvasRef,
  onDrawStart,
  onDrawMove,
  onDrawEnd,
  aiCaptureViewportRef,
  // Capture dialog
  captureGridOpen,
  onCaptureClose,
  videoRef,
  captureViewportRef,
  fallbackCameraInputRef,
  handleFallbackCaptureFile,
  videoScanInputRef,
  handleVideoScanFile,
  videoScanBusy,
  videoScanProgress,
  videoScanMessage,
  uploadPreparedBusy,
  uploadPreparedProgress,
  onUploadPreparedTiles,
  canUploadPreparedTiles,
  onDeleteUploadedTiles,
  deleteUploadedBusy,
  canDeleteUploadedTiles,
  preparedCount,
  scanDirection,
  scanMosaicPreviewUrl,
  scanAcceptedCount,
  scanRejectedBlurCount,
  scanRejectedMatchCount,
  gridRows,
  gridCols,
  setGridRows,
  setGridCols,
  totalTiles,
  capturedCount,
  capturePercent,
  currentTile,
  currentTileKey,
  currentRow,
  currentCol,
  capturedTiles,
  onSelectTile,
  captureFeedback,
  needsRetake,
  captureBusy,
  cameraReady,
  captureCurrentTile,
  removeCurrentTile,
  startStitchPipeline,
  stitchJobId,
  stitchBusy,
  stitchStatus,
  stitchPipelinePercent,
  stitchPipelinePhase,
  stitchPipelineMessage,
  ghostEnabled,
  ghostOpacity,
  setGhostEnabled,
  setGhostOpacity,
  overlapScore,
  overlapHint,
  referenceStripUrl,
  liveStripUrl,
  // Snackbar
  toast,
  setToast,
}) {
  const subjectLabel =
    slideSubject === "embryology"
      ? "Embryology"
      : slideSubject === "pathology"
        ? "Pathology"
        : "Histology";

  return (
    <>
      <Dialog open={aiOpen} onClose={onAiClose} fullScreen={isMobile} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          Ask AI — {subjectLabel} tutor
          <IconButton onClick={onAiClose} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <AiChatDialogContent
            slideSubject={slideSubject}
            aiError={aiError}
            captureLoading={captureLoading}
            capturedImage={capturedImage}
            aiMessages={aiMessages}
            aiQuestion={aiQuestion}
            setAiQuestion={setAiQuestion}
            onSend={onSend}
            aiLoading={aiLoading}
            captureLoadingForDisable={captureLoadingForDisable}
            isMobile={isMobile}
            chatMessagesRef={chatMessagesRef}
            hasDrawing={hasDrawing}
            onClearDrawing={onClearDrawing}
            getContextTitle={getContextTitle}
            setupDrawingCanvas={setupDrawingCanvas}
            baseImageRef={baseImageRef}
            drawCanvasRef={drawCanvasRef}
            onDrawStart={onDrawStart}
            onDrawMove={onDrawMove}
            onDrawEnd={onDrawEnd}
            aiCaptureViewportRef={aiCaptureViewportRef}
          />
        </DialogContent>
      </Dialog>

      <CaptureGridDialog
        open={captureGridOpen}
        onClose={onCaptureClose}
        isMobile={isMobile}
        videoRef={videoRef}
        captureViewportRef={captureViewportRef}
        fallbackCameraInputRef={fallbackCameraInputRef}
        handleFallbackCaptureFile={handleFallbackCaptureFile}
        videoScanInputRef={videoScanInputRef}
        handleVideoScanFile={handleVideoScanFile}
        videoScanBusy={videoScanBusy}
        videoScanProgress={videoScanProgress}
        videoScanMessage={videoScanMessage}
        uploadPreparedBusy={uploadPreparedBusy}
        uploadPreparedProgress={uploadPreparedProgress}
        onUploadPreparedTiles={onUploadPreparedTiles}
        canUploadPreparedTiles={canUploadPreparedTiles}
        onDeleteUploadedTiles={onDeleteUploadedTiles}
        deleteUploadedBusy={deleteUploadedBusy}
        canDeleteUploadedTiles={canDeleteUploadedTiles}
        preparedCount={preparedCount}
        scanDirection={scanDirection}
        scanMosaicPreviewUrl={scanMosaicPreviewUrl}
        scanAcceptedCount={scanAcceptedCount}
        scanRejectedBlurCount={scanRejectedBlurCount}
        scanRejectedMatchCount={scanRejectedMatchCount}
        gridRows={gridRows}
        gridCols={gridCols}
        setGridRows={setGridRows}
        setGridCols={setGridCols}
        totalTiles={totalTiles}
        capturedCount={capturedCount}
        capturePercent={capturePercent}
        currentTile={currentTile}
        currentTileKey={currentTileKey}
        currentRow={currentRow}
        currentCol={currentCol}
        capturedTiles={capturedTiles}
        onSelectTile={onSelectTile}
        captureFeedback={captureFeedback}
        needsRetake={needsRetake}
        captureBusy={captureBusy}
        cameraReady={cameraReady}
        captureCurrentTile={captureCurrentTile}
        removeCurrentTile={removeCurrentTile}
        startStitchPipeline={startStitchPipeline}
        stitchJobId={stitchJobId}
        stitchBusy={stitchBusy}
        stitchStatus={stitchStatus}
        stitchPipelinePercent={stitchPipelinePercent}
        stitchPipelinePhase={stitchPipelinePhase}
        stitchPipelineMessage={stitchPipelineMessage}
        ghostEnabled={ghostEnabled}
        ghostOpacity={ghostOpacity}
        setGhostEnabled={setGhostEnabled}
        setGhostOpacity={setGhostOpacity}
        overlapScore={overlapScore}
        overlapHint={overlapHint}
        referenceStripUrl={referenceStripUrl}
        liveStripUrl={liveStripUrl}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%", boxShadow: 3, borderRadius: 1.5 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

